import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool, Employee, Category, TrainingRecord, Curriculum, CurriculumCompletion, ProficiencyScale, ToolQuiz, QuizQuestion, Notification } from '../types';
import { Search, Star, Shield, ExternalLink, MessageSquare, Send, CheckCircle2, LayoutGrid, List, Plus, Lock, BookOpen, Tag, X, ChevronRight, AlertCircle, HelpCircle, GraduationCap, Calendar, Edit2, Save, FileText, CheckSquare, Trash2, ArrowLeft, Filter, AlertTriangle, FileWarning, MessageCircle, Users, Bell, TrendingUp, ShieldCheck, CheckCircle, Laptop } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { InfoTooltip } from '../components/InfoTooltip';
import { RatingSelector } from '../components/portal/RatingSelector';
import { ModalShell } from '../components/portal/ModalShell';

interface UserPortalViewProps {
  currentUser: Employee;
  tools: Tool[];
  employees: Employee[];
  curricula?: Curriculum[];
  curriculumCompletions?: CurriculumCompletion[];
  onCompleteCurriculum?: (employeeId: string, curriculumId: string, satisfaction: number) => void;
  onSubmitFeedback: (toolId: string, satisfaction: number, proficiency: number, feedback: string, isTrainingCompletion: boolean) => void;
  onRequestAccess: (toolId: string, reason: string, neededBy: string, mode?: 'new' | 'change') => void;
  onRequestNewTool: (data: any) => void;
  onRequestTraining: (toolId: string, needs: string) => void;
  trainingRecords: TrainingRecord[];
  onUpdateTool?: (tool: Tool) => void; // Optional for owner edits
  notifications?: Notification[]; // New prop for messages
  onUpdateNotification?: (notif: Notification) => void;
  onAccessTaskClosed?: (notif: Notification, newStatus: string) => void;
}

type PortalTab = 'access' | 'manage' | 'team';
type ManagerTab = 'matrix' | 'quizzes';

interface ActionItem {
    id: string;
    type: 'TRAINING' | 'REVIEW' | 'SETUP' | 'FEEDBACK' | 'CLARIFICATION' | 'ACCESS_PROVISION';
    tool?: Tool;
    label: string;
    priority: 'HIGH' | 'MEDIUM';
    notification?: Notification;
}

export const UserPortalView: React.FC<UserPortalViewProps> = ({ currentUser, tools, employees, curricula = [], curriculumCompletions = [], onCompleteCurriculum, onSubmitFeedback, onRequestAccess, onRequestNewTool, onRequestTraining, trainingRecords, onUpdateTool, notifications = [], onUpdateNotification, onAccessTaskClosed }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PortalTab>('access');
  const [searchTerm, setSearchTerm] = useState('');
  
  // New Filter States
  const [filterOwnership, setFilterOwnership] = useState<'ALL' | 'OWNED'>('ALL');
  const [filterTraining, setFilterTraining] = useState<'ALL' | 'REQUIRED'>('ALL');
  const [filterTag, setFilterTag] = useState<string>('All');

  // Modals state
  const [feedbackTool, setFeedbackTool] = useState<Tool | null>(null);
  const [detailTool, setDetailTool] = useState<Tool | null>(null);
  const [accessRequestTool, setAccessRequestTool] = useState<Tool | null>(null);
  const [trainingRequestTool, setTrainingRequestTool] = useState<Tool | null>(null);
  const [isNewToolModalOpen, setIsNewToolModalOpen] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [clarificationNotif, setClarificationNotif] = useState<Notification | null>(null);
  const [ownerTaskNotif, setOwnerTaskNotif] = useState<Notification | null>(null);
  
  // Owner Editing State inside Modal
  const [isEditingTraining, setIsEditingTraining] = useState(false);
  const [editNextDate, setEditNextDate] = useState('');
  
  // State to track if the current feedback session is for training completion
  const [isTrainingCompletion, setIsTrainingCompletion] = useState(false);
  // Curriculum being completed (score modal for this formation only)
  const [curriculumToComplete, setCurriculumToComplete] = useState<Curriculum | null>(null);
  
  // Form states
  const [rating, setRating] = useState(5);
  const [proficiency, setProficiency] = useState(5);
  const [comment, setComment] = useState('');
  const [userReply, setUserReply] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [accessReason, setAccessReason] = useState('');
  const [accessNeededBy, setAccessNeededBy] = useState('');
  const [accessRequestMode, setAccessRequestMode] = useState<'new' | 'change'>('new');

  const [trainingNeeds, setTrainingNeeds] = useState('');

  const [newToolData, setNewToolData] = useState({
    name: '',
    purpose: '',
    alternatives: '',
    priceEstimation: '',
    termsUrl: '',
    gdprCompliant: false
  });

  // --- MANAGEMENT TAB STATE ---
  const [selectedManagedTool, setSelectedManagedTool] = useState<Tool | null>(null);
  const [managerSubTab, setManagerSubTab] = useState<ManagerTab>('matrix');
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  // Quiz Builder State
  const [newQuizTitle, setNewQuizTitle] = useState('');
  const [newQuizQuestions, setNewQuizQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionText, setCurrentQuestionText] = useState('');

  // Reset edit state when tool changes
  useEffect(() => {
      if (detailTool) {
          setEditNextDate(detailTool.nextTrainingDate || '');
          setIsEditingTraining(false);
      }
  }, [detailTool]);

  // Tool has required training if it has user-audience curricula linked or legacy trainingUrl (admin curricula are not assigned to employees)
  const userCurricula = curricula.filter((c) => (c.audience ?? 'user') !== 'admin');
  const toolHasTraining = (tool: Tool) =>
    userCurricula.some((c) => c.toolId === tool.id) || !!tool.trainingUrl;
  const getCurriculaForTool = (toolId: string) => userCurricula.filter((c) => c.toolId === toolId);
  // Score is on the formation: completion is per curriculum
  const isCurriculumCompleted = (employeeId: string, curriculumId: string) =>
    curriculumCompletions.some((cc) => cc.employeeId === employeeId && cc.curriculumId === curriculumId);
  const getCompletionForCurriculum = (employeeId: string, curriculumId: string) =>
    curriculumCompletions.find((cc) => cc.employeeId === employeeId && cc.curriculumId === curriculumId);
  // Tool training complete = all its curricula completed (or legacy trainingRecords if no curricula)
  const isToolTrainingComplete = (employeeId: string, toolId: string) => {
    const forTool = getCurriculaForTool(toolId);
    if (forTool.length === 0)
      return trainingRecords.some((r) => r.employeeId === employeeId && r.toolId === toolId);
    return forTool.every((c) => isCurriculumCompleted(employeeId, c.id));
  };

  // Current user's latest feedback (scores) for a tool
  const getUserScoresForTool = (tool: Tool) => {
    if (!tool.feedback || tool.feedback.length === 0) return null;
    const mine = tool.feedback
      .filter((f) => f.userId === currentUser.id)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return mine[0] ?? null;
  };

  // Team average satisfaction for a curriculum (from all completions for that training)
  const getTeamAverageSatisfactionForCurriculum = (curriculumId: string) => {
    const completions = curriculumCompletions.filter((cc) => cc.curriculumId === curriculumId && cc.satisfaction != null);
    if (completions.length === 0) return null;
    const sum = completions.reduce((acc, c) => acc + (c.satisfaction ?? 0), 0);
    return sum / completions.length;
  };

  // --- LOGIC: ACTION ITEMS CALCULATION ---
  const actionItems: ActionItem[] = [];

  // 1. Clarification Requests (New)
  notifications.forEach(n => {
      if (n.requesterId === currentUser.id && n.status === 'CLARIFICATION_NEEDED') {
          actionItems.push({
              id: `clarify-${n.id}`,
              type: 'CLARIFICATION',
              label: `Admin requested info for: ${n.toolName || n.newToolData?.name || 'Request'}`,
              priority: 'HIGH',
              notification: n
          });
      }
  });

  // 2. Training Needed (from curricula or legacy trainingUrl)
  tools.forEach(tool => {
      const hasAccess = tool.assignedUserIds?.includes(currentUser.id) || tool.powerUserId === currentUser.id;
      if (hasAccess && toolHasTraining(tool)) {
          const isTrained = isToolTrainingComplete(currentUser.id, tool.id);
          if (!isTrained) {
              actionItems.push({
                  id: `train-${tool.id}`,
                  type: 'TRAINING',
                  tool,
                  label: `Complete training for ${tool.name}`,
                  priority: 'HIGH'
              });
          }
      }
  });

  // 3. Pending accesses (OWNER_TASK pending for this responsible)
  notifications
    .filter(n => n.type === 'OWNER_TASK' && n.status === 'PENDING')
    .forEach(n => {
      const tool = tools.find(t => t.id === n.toolId);
      if (tool?.powerUserId === currentUser.id) {
        actionItems.push({
          id: `owner-task-${n.id}`,
          type: 'ACCESS_PROVISION',
          label: `Provide access or close task: ${n.toolName || 'Tool'}`,
          priority: 'HIGH',
          notification: n,
          tool,
        });
      }
    });

  // 4. Owner Tasks (reviews, setup)
  tools.filter(t => t.powerUserId === currentUser.id).forEach(tool => {
      // Contract Review
      const daysUntilNotice = Math.ceil((new Date(tool.noticeDeadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
      if (daysUntilNotice < 30) {
          actionItems.push({
              id: `review-${tool.id}`,
              type: 'REVIEW',
              tool,
              label: `Review contract for ${tool.name} (Deadline in ${daysUntilNotice} days)`,
              priority: 'HIGH'
          });
      }
      // Setup: Scale
      if (!tool.proficiencyDefinitions || tool.proficiencyDefinitions.length === 0) {
          actionItems.push({
              id: `scale-${tool.id}`,
              type: 'SETUP',
              tool,
              label: `Define proficiency scale for ${tool.name}`,
              priority: 'MEDIUM'
          });
      }
      // Setup: Quiz
      if (!tool.quizzes || tool.quizzes.length === 0) {
           actionItems.push({
              id: `quiz-${tool.id}`,
              type: 'SETUP',
              tool,
              label: `Create certification exam for ${tool.name}`,
              priority: 'MEDIUM'
          });
      }
  });

  // --- LOGIC: FILTERING ---
  
  // All available tags for filter
  const allTags = Array.from(new Set(tools.flatMap(t => t.tags || []))).sort();

  // "My Tools": Owned by me OR Assigned to me
  const baseMyTools = tools.filter(t => t.powerUserId === currentUser.id || t.assignedUserIds?.includes(currentUser.id));
  
  const filteredMyTools = baseMyTools.filter(t => {
      const matchSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchOwner = filterOwnership === 'ALL' || (filterOwnership === 'OWNED' && t.powerUserId === currentUser.id);
      const matchTag = filterTag === 'All' || t.tags?.includes(filterTag);
      
      let matchTraining = true;
      if (filterTraining === 'REQUIRED') {
          const isTrained = isToolTrainingComplete(currentUser.id, t.id);
          matchTraining = toolHasTraining(t) && !isTrained;
      }

      return matchSearch && matchOwner && matchTag && matchTraining;
  });

  // "Other Tools": Not accessed by me
  const baseOtherTools = tools.filter(t => t.powerUserId !== currentUser.id && !t.assignedUserIds?.includes(currentUser.id));
  
  const filteredOtherTools = baseOtherTools.filter(t => {
      const matchSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchTag = filterTag === 'All' || t.tags?.includes(filterTag);
      // Ownership and Training filters don't really apply to "Browse" section in the same way, but let's apply Tag/Search
      return matchSearch && matchTag;
  });

  const ownedTools = tools.filter(t => t.powerUserId === currentUser.id);
  const directReports = employees.filter(e => e.managerId === currentUser.id);


  // --- Handlers ---

  const handleActionClick = (action: ActionItem) => {
      setIsActionModalOpen(false);
      if (action.type === 'ACCESS_PROVISION' && action.notification) {
          setOwnerTaskNotif(action.notification);
      } else if (action.type === 'CLARIFICATION' && action.notification) {
          setClarificationNotif(action.notification);
          setUserReply('');
      } else if (action.type === 'TRAINING' && action.tool) {
          setDetailTool(action.tool);
          setActiveTab('access');
      } else if (action.tool) {
          // Review or Setup - Go to Manager
          setSelectedManagedTool(action.tool);
          setActiveTab('manage');
          if (action.type === 'SETUP' && action.label.includes('scale')) {
              setManagerSubTab('matrix');
          } else if (action.type === 'SETUP' && action.label.includes('exam')) {
              setManagerSubTab('quizzes');
          }
      }
  };

  const handleSendClarification = () => {
      if (!clarificationNotif || !onUpdateNotification || !userReply.trim()) return;

      const newHistoryItem = {
          senderId: currentUser.id,
          senderName: currentUser.name,
          role: 'user' as const,
          message: userReply,
          date: new Date().toISOString()
      };

      const updatedNotif: Notification = {
          ...clarificationNotif,
          status: 'PENDING', // Send back to admin queue
          history: [...(clarificationNotif.history || []), newHistoryItem]
      };

      onUpdateNotification(updatedNotif);
      setClarificationNotif(null);
      setUserReply('');
  };

  const handleSaveTrainingDate = () => {
      if (detailTool && onUpdateTool) {
          const updated = { ...detailTool, nextTrainingDate: editNextDate };
          onUpdateTool(updated);
          setDetailTool(updated); // Update local state immediately
          setIsEditingTraining(false);
      }
  };

  const handleSubmitFeedback = (e: React.FormEvent) => {
    e.preventDefault();
    if (feedbackTool) {
      onSubmitFeedback(feedbackTool.id, rating, proficiency, comment, isTrainingCompletion);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFeedbackTool(null);
        setRating(5);
        setProficiency(5);
        setComment('');
        setIsTrainingCompletion(false); // Reset context
      }, 2000);
    }
  };

  const handleSubmitAccess = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessRequestTool) {
      onRequestAccess(accessRequestTool.id, accessReason, accessNeededBy, accessRequestMode);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setAccessRequestTool(null);
        setAccessReason('');
        setAccessNeededBy('');
        setAccessRequestMode('new');
      }, 2000);
    }
  };

  const handleSubmitTrainingRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (trainingRequestTool) {
        onRequestTraining(trainingRequestTool.id, trainingNeeds);
        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            setTrainingRequestTool(null);
            setTrainingNeeds('');
        }, 2000);
    }
  };

  const handleSubmitNewTool = (e: React.FormEvent) => {
    e.preventDefault();
    onRequestNewTool(newToolData);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setIsNewToolModalOpen(false);
      setNewToolData({
        name: '',
        purpose: '',
        alternatives: '',
        priceEstimation: '',
        termsUrl: '',
        gdprCompliant: false
      });
    }, 2000);
  };

  const handleCurriculumScoreSubmit = () => {
    if (!curriculumToComplete || !onCompleteCurriculum) return;
    onCompleteCurriculum(currentUser.id, curriculumToComplete.id, rating);
    setCurriculumToComplete(null);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  // --- Manager Tab Handlers ---

  const handleUpdateProficiencyDefinition = (level: number, title: string, description: string) => {
      if (!selectedManagedTool || !onUpdateTool) return;
      
      const currentDefs = selectedManagedTool.proficiencyDefinitions || [];
      const filtered = currentDefs.filter(d => d.level !== level);
      const newDefs = [...filtered, { level, title, description }].sort((a,b) => a.level - b.level);
      
      const updatedTool = { ...selectedManagedTool, proficiencyDefinitions: newDefs };
      onUpdateTool(updatedTool);
      setSelectedManagedTool(updatedTool);
  };

  const handleAddQuestion = () => {
      if (!currentQuestionText) return;
      const newQ: QuizQuestion = {
          id: crypto.randomUUID(),
          text: currentQuestionText,
          type: 'text'
      };
      setNewQuizQuestions([...newQuizQuestions, newQ]);
      setCurrentQuestionText('');
  };

  const handleSaveQuiz = () => {
      if (!selectedManagedTool || !onUpdateTool || !newQuizTitle || newQuizQuestions.length === 0) return;

      const newQuiz: ToolQuiz = {
          id: crypto.randomUUID(),
          title: newQuizTitle,
          questions: newQuizQuestions,
          createdAt: new Date().toISOString()
      };

      const updatedQuizzes = [...(selectedManagedTool.quizzes || []), newQuiz];
      const updatedTool = { ...selectedManagedTool, quizzes: updatedQuizzes };
      
      onUpdateTool(updatedTool);
      setSelectedManagedTool(updatedTool);
      setIsQuizModalOpen(false);
      setNewQuizTitle('');
      setNewQuizQuestions([]);
  };

  const handleDeleteQuiz = (quizId: string) => {
       if (!selectedManagedTool || !onUpdateTool) return;
       if (!window.confirm("Are you sure you want to delete this exam?")) return;

       const updatedQuizzes = selectedManagedTool.quizzes?.filter(q => q.id !== quizId) || [];
       const updatedTool = { ...selectedManagedTool, quizzes: updatedQuizzes };
       onUpdateTool(updatedTool);
       setSelectedManagedTool(updatedTool);
  };

  const getOwnerName = (ownerId?: string) => {
      if (!ownerId) return 'Unassigned';
      return employees.find(e => e.id === ownerId)?.name || 'Unknown';
  };

  return (
    <div className="space-y-6 animate-fade-in pb-12 relative">
        {/* Banner Section - Always at top of portal */}
        {activeTab === 'access' && (
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 text-white shadow-lg mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">Welcome back, {currentUser.name.split(' ')[0]}!</h1>
                        <p className="text-indigo-100 opacity-90">
                            You have access to <span className="font-bold bg-white/20 px-2 py-0.5 rounded">{baseMyTools.length} tools</span> in the company stack.
                        </p>
                    </div>
                    <div className="flex gap-4">
                        <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center min-w-[120px]">
                            <div className="text-3xl font-bold">{baseMyTools.length}</div>
                            <div className="text-xs text-indigo-200 uppercase font-bold tracking-wider mt-1">My Access</div>
                        </div>
                        
                        <div 
                            onClick={() => setIsActionModalOpen(true)}
                            className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center min-w-[120px] cursor-pointer hover:bg-white/20 transition-colors relative"
                        >
                            <div className="text-3xl font-bold">{actionItems.length}</div>
                            <div className="text-xs text-indigo-200 uppercase font-bold tracking-wider mt-1">Pending Actions</div>
                            {actionItems.length > 0 && (
                                <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Filters & Actions */}
        {activeTab === 'access' && (
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0">
                    <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                        <span className="text-xs font-bold text-slate-500 px-2 flex items-center gap-1"><Filter size={12}/> Filter:</span>
                        <select 
                            value={filterTag}
                            onChange={(e) => setFilterTag(e.target.value)}
                            className="bg-white text-slate-700 text-sm border-none rounded px-3 py-1.5 focus:ring-0 cursor-pointer"
                        >
                            <option value="All">All Tags</option>
                            {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                        </select>
                    </div>

                    <button 
                        onClick={() => setFilterOwnership(filterOwnership === 'ALL' ? 'OWNED' : 'ALL')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${filterOwnership === 'OWNED' ? 'bg-toggle text-white border-toggle' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    >
                        {filterOwnership === 'OWNED' ? 'Owned by Me' : 'Ownership: All'}
                    </button>

                    <button 
                        onClick={() => setFilterTraining(filterTraining === 'ALL' ? 'REQUIRED' : 'ALL')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${filterTraining === 'REQUIRED' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    >
                        {filterTraining === 'REQUIRED' ? 'Training Required' : 'Training: All'}
                    </button>
                </div>

                <div className="flex gap-2 w-full lg:w-auto">
                    <div className="relative flex-1 lg:w-64">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                        <input 
                            type="text" 
                            placeholder="Search my tools..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        />
                    </div>
                    <button 
                        onClick={() => setIsNewToolModalOpen(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 shadow-sm transition-colors text-sm whitespace-nowrap"
                    >
                        <Plus size={16} />
                        New Request
                    </button>
                    <button
                        onClick={() => navigate('/portal/help')}
                        className="bg-white hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 border border-slate-200 transition-colors text-sm whitespace-nowrap"
                    >
                        <HelpCircle size={16} />
                        Help
                    </button>
                </div>
            </div>
        )}

        {/* Tab Navigation (Moved below banner/filter for 'Access' flow, or acts as main nav) */}
        <div className="flex justify-between items-center mt-6 mb-4">
            <div className="bg-slate-200 p-1 rounded-xl inline-flex">
                <button
                    onClick={() => setActiveTab('access')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        activeTab === 'access' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <LayoutGrid size={16} /> My Access
                </button>
                <button
                    onClick={() => setActiveTab('manage')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        activeTab === 'manage' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <Shield size={16} /> Managed Tools
                    {ownedTools.length > 0 && <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{ownedTools.length}</span>}
                    <span onClick={(e) => e.stopPropagation()}><InfoTooltip text="Tools you're responsible for. Manage access and track user proficiency." /></span>
                </button>
                {directReports.length > 0 && (
                    <button
                        onClick={() => setActiveTab('team')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            activeTab === 'team' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        <Users size={16} /> My Team
                        <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{directReports.length}</span>
                    </button>
                )}
            </div>
        </div>

      {activeTab === 'access' ? (
        <>
            {/* My Tools Section */}
            {baseMyTools.length > 0 && (
                <section>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Shield className="text-indigo-600" size={24} />
                    My Tools & Training
                    <InfoTooltip text="Tools assigned to you. Click one for details, training materials, or to leave feedback." />
                    <button
                      onClick={() => navigate('/portal/help')}
                      className="ml-2 text-xs font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                    >
                      Help <ChevronRight size={12} />
                    </button>
                </h2>
                {filteredMyTools.length === 0 ? (
                    <div className="bg-white rounded-xl border border-dashed border-slate-200">
                      <EmptyState
                        icon={<Search size={40} />}
                        title="No tools match"
                        subtitle="Try adjusting your search or filters to find what you're looking for."
                      />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredMyTools.map(tool => {
                        const isTrained = isToolTrainingComplete(currentUser.id, tool.id);
                        const isOwner = tool.powerUserId === currentUser.id;
                        const needsTraining = toolHasTraining(tool) && !isTrained;
                        const myScores = getUserScoresForTool(tool);

                        return (
                        <div 
                            key={tool.id} 
                            onClick={() => setDetailTool(tool)}
                            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all cursor-pointer relative overflow-hidden group active:scale-[0.99]"
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <Shield size={80} />
                            </div>
                            <div className="relative z-10">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-12 h-12 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-lg uppercase">
                                        {tool.name.substring(0, 2)}
                                    </div>
                                    {isOwner && (
                                        <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-1 rounded-full">
                                            Owner
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 mb-1">{tool.name}</h3>
                                <p className="text-sm text-slate-500 mb-3">{tool.category}</p>
                                
                                {tool.tags && tool.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-4">
                                        {tool.tags.slice(0, 3).map(tag => (
                                            <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium flex items-center gap-0.5">
                                                <Tag size={8} /> {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                
                                {(toolHasTraining(tool)) && (
                                    <div className={`text-xs font-bold inline-flex items-center gap-1 px-2 py-1 rounded mb-4 ${needsTraining ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                                        {needsTraining ? (
                                            <>
                                                <AlertCircle size={12} />
                                                Pending training
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 size={12} />
                                                Training complete
                                            </>
                                        )}
                                    </div>
                                )}

                                {myScores && (
                                    <div className="text-xs text-slate-600 mb-3 flex items-center gap-2">
                                        <Star size={10} className="text-amber-500 fill-amber-500" />
                                        <span>Your score: Sat <strong>{myScores.satisfaction}</strong>/10</span>
                                        <span className="text-slate-300">|</span>
                                        <TrendingUp size={10} className="text-emerald-500" />
                                        <span>Prof <strong>{myScores.proficiency}</strong>/10</span>
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <button 
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setFeedbackTool(tool); 
                                            setIsTrainingCompletion(false);
                                        }}
                                        className="flex-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 py-2 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Give Score
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setAccessRequestMode('change');
                                            setAccessReason('');
                                            setAccessNeededBy('');
                                            setAccessRequestTool(tool);
                                        }}
                                        className="flex-1 bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 py-2 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Change Access
                                    </button>
                                </div>
                            </div>
                        </div>
                        );
                    })}
                    </div>
                )}
                </section>
            )}

            {/* Browse All Tools */}
            <section className="mt-8 pt-8 border-t border-slate-200">
                <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 mb-6">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <LayoutGrid className="text-slate-400" size={24} />
                        Company Stack Directory
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {filteredOtherTools.map(tool => (
                        <div key={tool.id} onClick={() => setDetailTool(tool)} className="bg-white rounded-xl p-4 border border-slate-200 hover:border-indigo-300 transition-colors flex flex-col h-full opacity-80 hover:opacity-100 cursor-pointer">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-bold text-slate-800">{tool.name}</div>
                                <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">{tool.category}</div>
                            </div>
                            {tool.tags && tool.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {tool.tags.slice(0, 3).map(tag => (
                                        <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-slate-500 mb-4 flex-1 truncate">
                                {tool.description || tool.notes || 'No description available.'}
                            </p>
                            <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                                <div className="flex items-center gap-1 text-slate-400">
                                    <Star size={12} className={tool.satisfactionScore > 80 ? "fill-amber-400 text-amber-400" : ""} />
                                    <span className="text-xs font-medium">{tool.satisfactionScore/10}/10</span>
                                </div>
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAccessRequestMode('new');
                                        setAccessReason('');
                                        setAccessNeededBy('');
                                        setAccessRequestTool(tool);
                                    }}
                                    className="text-xs font-bold text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-3 py-1.5 rounded-md hover:bg-indigo-50 flex items-center gap-1"
                                >
                                    <Lock size={10} />
                                    Request Access
                                </button>
                            </div>
                        </div>
                    ))}
                    {filteredOtherTools.length === 0 && (
                        <div className="col-span-full p-8 text-center text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                            No other tools found matching your search.
                        </div>
                    )}
                </div>
            </section>
        </>
      ) : activeTab === 'manage' ? (
        /* --- MANAGER TAB CONTENT --- */
        <div className="flex flex-col lg:flex-row gap-8 animate-in slide-in-from-right-4 duration-300">
            {/* Left Sidebar: Tool List */}
            <div className="w-full lg:w-1/4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Your Tools</h3>
                <div className="space-y-3">
                    {ownedTools.length === 0 ? (
                        <EmptyState
                          icon={<Laptop size={40} />}
                          title="You don't manage any tools"
                          subtitle="When you're set as the responsible person for a tool, it will appear here."
                        />
                    ) : (
                        ownedTools.map(tool => (
                            <div 
                                key={tool.id}
                                onClick={() => setSelectedManagedTool(tool)}
                                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                                    selectedManagedTool?.id === tool.id 
                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' 
                                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                <div className="font-bold">{tool.name}</div>
                                <div className={`text-xs mt-1 ${selectedManagedTool?.id === tool.id ? 'text-indigo-200' : 'text-slate-400'}`}>
                                    {tool.category}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Right Content: Manager Interface */}
            <div className="flex-1">
                {selectedManagedTool ? (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 min-h-[500px] flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">{selectedManagedTool.name} Manager</h2>
                                <p className="text-sm text-slate-500">Define competency standards and create assessments.</p>
                            </div>
                            <div className="flex bg-white rounded-lg border border-slate-200 p-1">
                                <button 
                                    onClick={() => setManagerSubTab('matrix')}
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${managerSubTab === 'matrix' ? 'bg-toggle text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Competency Matrix
                                </button>
                                <button 
                                    onClick={() => setManagerSubTab('quizzes')}
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${managerSubTab === 'quizzes' ? 'bg-toggle text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Exam Builder
                                </button>
                            </div>
                        </div>

                        <div className="p-6 flex-1">
                            {managerSubTab === 'matrix' ? (
                                <div className="space-y-6">
                                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg text-sm text-blue-800 mb-6">
                                        <p className="font-bold flex items-center gap-2 mb-1"><BookOpen size={16}/> Define Proficiency Levels</p>
                                        Use this scale to help users self-assess accurately (1-10). Define key milestones.
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {[1, 3, 5, 7, 10].map(level => {
                                            const def = selectedManagedTool.proficiencyDefinitions?.find(d => d.level === level) || { level, title: '', description: '' };
                                            return (
                                                <div key={level} className="border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 font-bold flex items-center justify-center text-sm border border-slate-200">
                                                            {level}
                                                        </span>
                                                        <span className="text-xs text-slate-400 uppercase font-bold tracking-wider">
                                                            {level === 1 ? 'Novice' : level === 10 ? 'Expert' : `Level ${level}`}
                                                        </span>
                                                    </div>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Short Title (e.g. Can Login)"
                                                        className="w-full text-sm font-bold text-slate-800 border-none focus:ring-0 px-0 mb-1 placeholder:text-slate-300"
                                                        value={def.title}
                                                        onChange={(e) => handleUpdateProficiencyDefinition(level, e.target.value, def.description)}
                                                    />
                                                    <textarea 
                                                        placeholder="Description of skills required..."
                                                        className="w-full text-xs text-slate-600 border border-slate-100 rounded p-2 bg-slate-50 focus:bg-white focus:border-indigo-300 outline-none resize-none h-20"
                                                        value={def.description}
                                                        onChange={(e) => handleUpdateProficiencyDefinition(level, def.title, e.target.value)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="font-bold text-slate-800">Available Exams</h3>
                                        <button 
                                            onClick={() => setIsQuizModalOpen(true)}
                                            className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                                        >
                                            <Plus size={16} /> Create Exam
                                        </button>
                                    </div>

                                    {!selectedManagedTool.quizzes || selectedManagedTool.quizzes.length === 0 ? (
                                        <EmptyState
                                          icon={<FileText size={40} />}
                                          title="No exams yet"
                                          subtitle="The admin hasn't created exams for this tool yet."
                                        />
                                    ) : (
                                        <div className="space-y-3">
                                            {selectedManagedTool.quizzes.map(quiz => (
                                                <div key={quiz.id} className="p-4 border border-slate-200 rounded-lg flex justify-between items-center bg-white hover:border-indigo-200 transition-colors">
                                                    <div>
                                                        <div className="font-bold text-slate-800">{quiz.title}</div>
                                                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                                                            <span className="flex items-center gap-1"><CheckSquare size={12}/> {quiz.questions.length} Questions</span>
                                                            <span>•</span>
                                                            <span>Created {new Date(quiz.createdAt).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {/* Viewing/Editing existing quizzes implies a more complex UI, skipping for MVP brevity, offering delete */}
                                                        <button 
                                                            onClick={() => handleDeleteQuiz(quiz.id)}
                                                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                            title="Delete Exam"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 bg-white rounded-xl border border-dashed border-slate-200 p-12">
                        <Shield size={64} className="mb-4 text-slate-200" />
                        <h3 className="text-lg font-medium text-slate-600">Select a tool to manage</h3>
                        <p className="text-sm">Select one of your assigned tools from the sidebar.</p>
                    </div>
                )}
            </div>
        </div>
      ) : (
        /* --- TEAM TAB CONTENT --- */
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-300">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Users className="text-indigo-600" size={24} />
                My Direct Reports
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {directReports.map(report => {
                    const pendingTraining = tools.filter(t => {
                        const assigned = t.assignedUserIds?.includes(report.id) || t.powerUserId === report.id;
                        const needs = toolHasTraining(t);
                        const done = isToolTrainingComplete(report.id, t.id);
                        return assigned && needs && !done;
                    });

                    const pendingRequests = notifications.filter(n => n.requesterId === report.id && n.status === 'PENDING');

                    return (
                        <div key={report.id} className="p-6 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors shadow-sm">
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-lg">
                                        {report.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-800 text-lg">{report.name}</h4>
                                        <p className="text-sm text-slate-500">{report.role}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    {pendingTraining.length > 0 && (
                                        <div className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                                            <GraduationCap size={12} /> {pendingTraining.length} Training
                                        </div>
                                    )}
                                    {pendingRequests.length > 0 && (
                                        <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                                            <Bell size={12} /> {pendingRequests.length} Requests
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="bg-slate-50 p-4 rounded-lg">
                                    <span className="block text-xs font-bold text-slate-400 uppercase mb-3">Training Gaps</span>
                                    {pendingTraining.length === 0 ? (
                                        <div className="flex items-center gap-2 text-green-600">
                                            <CheckCircle2 size={16} />
                                            <span className="text-xs font-medium">All caught up</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {pendingTraining.slice(0, 3).map(t => (
                                                <div key={t.id} className="flex items-center gap-2 text-slate-700">
                                                    <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />
                                                    <span className="truncate">{t.name}</span>
                                                </div>
                                            ))}
                                            {pendingTraining.length > 3 && (
                                                <span className="text-xs text-slate-400 pl-6">+{pendingTraining.length - 3} more</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-slate-50 p-4 rounded-lg">
                                    <span className="block text-xs font-bold text-slate-400 uppercase mb-3">Active Requests</span>
                                    {pendingRequests.length === 0 ? (
                                        <span className="text-slate-400 italic text-xs">No pending requests</span>
                                    ) : (
                                        <div className="space-y-2">
                                            {pendingRequests.slice(0, 3).map(n => (
                                                <div key={n.id} className="flex items-center gap-2 text-slate-700">
                                                    <FileText size={14} className="text-blue-500 flex-shrink-0" />
                                                    <span className="truncate">
                                                        {n.type === 'NEW_TOOL_REQUEST' ? 'New Tool' : n.toolName || 'Access'}
                                                    </span>
                                                </div>
                                            ))}
                                            {pendingRequests.length > 3 && (
                                                <span className="text-xs text-slate-400 pl-6">+{pendingRequests.length - 3} more</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
      )}

      {/* ---------------- MODALS ---------------- */}

      {/* CLARIFICATION MODAL */}
      {clarificationNotif && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                  <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                      <div>
                          <h3 className="text-lg font-bold text-slate-800">Respond to Request</h3>
                          <p className="text-sm text-slate-500">Admin needs more information.</p>
                      </div>
                      <button onClick={() => setClarificationNotif(null)} className="text-slate-400 hover:text-slate-600">
                          <X size={24} />
                      </button>
                  </div>
                  <div className="p-6">
                      {clarificationNotif.history && (
                          <div className="mb-6 space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-100 max-h-48 overflow-y-auto">
                              {clarificationNotif.history.map((msg, idx) => (
                                  <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                                      <div className={`p-3 rounded-lg text-sm max-w-[80%] ${msg.role === 'user' ? 'bg-indigo-100 text-indigo-900 rounded-br-none' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-none'}`}>
                                          <span className="block text-[10px] font-bold opacity-50 mb-1">{msg.senderName} • {new Date(msg.date).toLocaleDateString()}</span>
                                          {msg.message}
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}
                      
                      <textarea 
                          className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-24 resize-none mb-4"
                          placeholder="Type your reply here..."
                          value={userReply}
                          onChange={(e) => setUserReply(e.target.value)}
                      />
                      
                      <div className="flex justify-end gap-3">
                          <button 
                            onClick={() => setClarificationNotif(null)}
                            className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                          >
                              Cancel
                          </button>
                          <button 
                            onClick={handleSendClarification}
                            disabled={!userReply.trim()}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                          >
                              Send Reply
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* ACCESS TO PROVIDE (OWNER TASK) MODAL */}
      {ownerTaskNotif && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                      <div>
                          <h3 className="text-lg font-bold text-slate-800">Pending accesses</h3>
                          <p className="text-sm text-slate-500">{ownerTaskNotif.toolName}</p>
                      </div>
                      <button onClick={() => setOwnerTaskNotif(null)} className="text-slate-400 hover:text-slate-600">
                          <X size={24} />
                      </button>
                  </div>
                  <div className="p-6">
                      <p className="text-slate-700 mb-6">
                          {ownerTaskNotif.accessReason || ownerTaskNotif.trainingNeeds || 'Task for tool owner.'}
                      </p>
                      <p className="text-xs text-slate-500 mb-4">Close this task (no other action).</p>
                      <div className="flex gap-3">
                          <button 
                              onClick={async () => {
                                  if (!onUpdateNotification) return;
                                  const updated = { ...ownerTaskNotif, status: 'DONE' as const };
                                  onUpdateNotification(updated);
                                  onAccessTaskClosed?.(ownerTaskNotif, 'DONE');
                                  setOwnerTaskNotif(null);
                              }}
                              className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex justify-center items-center gap-2"
                          >
                              <CheckCircle2 size={18} /> Done
                          </button>
                          <button 
                              onClick={async () => {
                                  if (!onUpdateNotification) return;
                                  const updated = { ...ownerTaskNotif, status: 'ABORTED' as const };
                                  onUpdateNotification(updated);
                                  onAccessTaskClosed?.(ownerTaskNotif, 'ABORTED');
                                  setOwnerTaskNotif(null);
                              }}
                              className="flex-1 py-2.5 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors flex justify-center items-center gap-2"
                          >
                              <X size={18} /> Aborted
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* ACTION ITEMS MODAL */}
      {isActionModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                  <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                      <div>
                          <h3 className="text-lg font-bold text-slate-800">Required Actions</h3>
                          <p className="text-sm text-slate-500">Tasks needing your attention.</p>
                      </div>
                      <button onClick={() => setIsActionModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                          <X size={24} />
                      </button>
                  </div>
                  <div className="p-6 overflow-y-auto">
                      {actionItems.length === 0 ? (
                          <EmptyState
                            icon={<CheckCircle size={40} />}
                            title="You're all set"
                            subtitle="No pending tasks right now. Check back later for new training or requests."
                            positive
                          />
                      ) : (
                          <div className="space-y-3">
                              {actionItems.map(action => (
                                  <div 
                                    key={action.id} 
                                    onClick={() => handleActionClick(action)}
                                    className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer group bg-white"
                                  >
                                      <div className={`p-2 rounded-full flex-shrink-0 ${
                                          action.type === 'TRAINING' ? 'bg-amber-100 text-amber-600' :
                                          action.type === 'REVIEW' ? 'bg-red-100 text-red-600' :
                                          action.type === 'CLARIFICATION' ? 'bg-purple-100 text-purple-600' :
                                          action.type === 'ACCESS_PROVISION' ? 'bg-indigo-100 text-indigo-600' :
                                          'bg-blue-100 text-blue-600'
                                      }`}>
                                          {action.type === 'TRAINING' && <GraduationCap size={18} />}
                                          {action.type === 'REVIEW' && <FileWarning size={18} />}
                                          {action.type === 'SETUP' && <AlertTriangle size={18} />}
                                          {action.type === 'CLARIFICATION' && <MessageCircle size={18} />}
                                          {action.type === 'ACCESS_PROVISION' && <ShieldCheck size={18} />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                          <div className="flex justify-between items-center gap-2">
                                              <span className="text-xs font-bold text-slate-400 uppercase">{action.type}</span>
                                              {action.priority === 'HIGH' && <span className="text-[10px] font-bold bg-red-100 text-red-600 px-2 rounded-full shrink-0">Urgent</span>}
                                          </div>
                                          {(action.tool || action.notification?.toolName) && (
                                              <p className="text-xs font-medium text-indigo-600 mt-0.5">Tool: {action.tool?.name ?? action.notification?.toolName ?? action.notification?.newToolData?.name ?? '—'}</p>
                                          )}
                                          <p className="font-medium text-slate-800 group-hover:text-indigo-700 mt-0.5">{action.label}</p>
                                          <p className="text-xs text-slate-500 mt-1">Click to resolve</p>
                                      </div>
                                      <div className="self-center">
                                          <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-400" />
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* Detail Modal */}
      {detailTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
             <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-6 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-lg uppercase">
                            {detailTool.name.substring(0, 2)}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-800">{detailTool.name}</h2>
                            <p className="text-xs text-slate-500">{detailTool.category}</p>
                        </div>
                    </div>
                    <button onClick={() => setDetailTool(null)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {/* Owner & Scores Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                         <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                             <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Tool Owner</h4>
                             <div className="flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 font-bold text-xs">
                                     {getOwnerName(detailTool.powerUserId).charAt(0)}
                                 </div>
                                 <div>
                                     <div className="text-sm font-bold text-slate-800">{getOwnerName(detailTool.powerUserId)}</div>
                                     <div className="text-xs text-slate-500">Responsible for access & training</div>
                                 </div>
                             </div>
                         </div>
                         <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center justify-around">
                             <div className="text-center">
                                 <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Satisfaction</h4>
                                 <div className="text-xl font-bold text-slate-800">{detailTool.satisfactionScore/10}<span className="text-xs text-slate-400">/10</span></div>
                             </div>
                             <div className="w-px h-8 bg-slate-200"></div>
                             <div className="text-center">
                                 <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Team Literacy</h4>
                                 <div className="text-xl font-bold text-slate-800">{detailTool.teamLiteracy}<span className="text-xs text-slate-400">/10</span></div>
                             </div>
                         </div>
                    </div>

                    {/* Your scores (given by the current user) */}
                    {(() => {
                        const myScores = getUserScoresForTool(detailTool);
                        if (!myScores) return null;
                        return (
                            <div className="mb-8 p-4 rounded-xl border-2 border-indigo-100 bg-indigo-50/50">
                                <h4 className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-3">Your scores</h4>
                                <div className="flex items-center gap-6 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <Star size={18} className="text-amber-500 fill-amber-500" />
                                        <span className="text-slate-700">Satisfaction: <strong>{myScores.satisfaction}</strong>/10</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <TrendingUp size={18} className="text-emerald-500" />
                                        <span className="text-slate-700">Proficiency: <strong>{myScores.proficiency}</strong>/10</span>
                                    </div>
                                    {myScores.date && (
                                        <span className="text-xs text-slate-500 ml-auto">
                                            {new Date(myScores.date).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Proficiency Scale Display if Available */}
                    {detailTool.proficiencyDefinitions && detailTool.proficiencyDefinitions.length > 0 && (
                        <div className="mb-8">
                             <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 flex items-center gap-2">
                                 <BookOpen size={14} className="text-indigo-600" /> Competency Matrix
                             </h4>
                             <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                 {detailTool.proficiencyDefinitions.slice(0, 3).map(def => (
                                     <div key={def.level} className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg">
                                         <div className="flex items-center gap-2 mb-1">
                                             <span className="font-bold text-indigo-700 text-sm">Level {def.level}</span>
                                             {def.title && <span className="text-xs text-slate-500">- {def.title}</span>}
                                         </div>
                                         <p className="text-xs text-slate-600 leading-tight">{def.description}</p>
                                     </div>
                                 ))}
                             </div>
                        </div>
                    )}

                    <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                        {detailTool.description || detailTool.notes || 'No detailed description available for this tool.'}
                    </p>

                    {detailTool.tags && detailTool.tags.length > 0 && (
                        <div className="mb-6">
                            <h4 className="text-xs font-bold text-slate-700 uppercase mb-2">Tags</h4>
                            <div className="flex flex-wrap gap-2">
                                {detailTool.tags.map(tag => (
                                    <span key={tag} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-medium flex items-center gap-1">
                                        <Tag size={10} /> {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Feedback Section */}
                    <div className="mb-6">
                         <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 flex items-center gap-2">
                             <MessageSquare size={14} className="text-indigo-600" />
                             Team Feedback
                         </h4>
                         {detailTool.feedback && detailTool.feedback.length > 0 ? (
                             <div className="space-y-3 max-h-40 overflow-y-auto pr-2 scrollbar-thin">
                                 {detailTool.feedback.filter(f => f.comment).map((fb, idx) => (
                                     <div key={idx} className="p-3 bg-white border border-slate-100 rounded-lg text-sm">
                                         <p className="text-slate-600 italic">"{fb.comment}"</p>
                                         <div className="flex justify-between mt-2 text-xs text-slate-400">
                                             <span>{fb.userName}</span>
                                             <span>{new Date(fb.date).toLocaleDateString()}</span>
                                         </div>
                                     </div>
                                 ))}
                             </div>
                         ) : (
                             <p className="text-sm text-slate-400 italic">No feedback yet. Use a tool and leave your review.</p>
                         )}
                    </div>

                    {detailTool.website && (
                         <div className="mb-6">
                             <a 
                                href={`https://${detailTool.website}`} 
                                target="_blank" 
                                rel="noreferrer"
                                className="flex items-center gap-2 text-indigo-600 hover:underline text-sm font-medium"
                            >
                                <ExternalLink size={14} /> Visit Website
                            </a>
                        </div>
                    )}

                    {(getCurriculaForTool(detailTool.id).length > 0 || detailTool.trainingUrl) ? (
                         <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <div className="flex justify-between items-start mb-3">
                                <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                    <BookOpen size={16} className="text-indigo-600" /> 
                                    {isToolTrainingComplete(currentUser.id, detailTool.id)
                                      ? 'Training complete'
                                      : 'Pending trainings'}
                                </h4>
                                {detailTool.nextTrainingDate && !getCurriculaForTool(detailTool.id).length && (
                                    <div className="flex items-center gap-1.5 bg-white border border-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs font-medium shadow-sm">
                                        <Calendar size={12} />
                                        Next Session: {new Date(detailTool.nextTrainingDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>

                            {/* List of curricula: each has its own score (on the formation) */}
                            {getCurriculaForTool(detailTool.id).length > 0 && (
                                <ul className="space-y-2 mb-4">
                                    {getCurriculaForTool(detailTool.id).map((cur) => {
                                        const completed = getCompletionForCurriculum(currentUser.id, cur.id);
                                        return (
                                        <li key={cur.id} className="flex items-center justify-between gap-2 bg-white p-3 rounded-lg border border-slate-100">
                                            <div className="min-w-0 flex-1">
                                                <span className="text-sm font-medium text-slate-800 block truncate">{cur.name}</span>
                                                {cur.description && <span className="text-xs text-slate-500 block truncate">{cur.description}</span>}
                                                {completed && (
                                                    <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                                                        <span className="text-green-600 inline-flex items-center gap-1">
                                                            <CheckCircle2 size={10} /> Done — Your satisfaction: <strong>{completed.satisfaction ?? '–'}</strong>/10
                                                        </span>
                                                        {getTeamAverageSatisfactionForCurriculum(cur.id) != null && (
                                                            <span className="block text-slate-500">Team average: {getTeamAverageSatisfactionForCurriculum(cur.id)!.toFixed(1)}/10</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {cur.url && (
                                                    <a href={cur.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-600 hover:underline text-sm font-medium">
                                                        Open <ExternalLink size={12} />
                                                    </a>
                                                )}
                                                {completed ? (
                                                    <span className="text-xs text-slate-400">Completed</span>
                                                ) : onCompleteCurriculum ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurriculumToComplete(cur)}
                                                        className="text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-1 rounded"
                                                    >
                                                        Mark as done
                                                    </button>
                                                ) : null}
                                            </div>
                                        </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {/* Legacy single training URL if no curricula */}
                            {detailTool.trainingUrl && getCurriculaForTool(detailTool.id).length === 0 && (
                                <>
                                    <div className="mb-4">
                                        <a 
                                            href={detailTool.trainingUrl} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            className="text-sm text-indigo-600 hover:underline font-medium flex items-center gap-1"
                                        >
                                            Open Training Module <ChevronRight size={14} />
                                        </a>
                                    </div>
                                    {/* Legacy: single Mark as done for tool (opens feedback modal, score stored per tool in trainingRecords) */}
                                    <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100">
                                        <span className="text-sm text-slate-600">Mark as done when finished (score applies to this training).</span>
                                        <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                                            <input 
                                                type="checkbox" 
                                                checked={trainingRecords.some(r => r.employeeId === currentUser.id && r.toolId === detailTool.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setFeedbackTool(detailTool);
                                                        setIsTrainingCompletion(true);
                                                        setDetailTool(null);
                                                    }
                                                }}
                                                disabled={trainingRecords.some(r => r.employeeId === currentUser.id && r.toolId === detailTool.id)} 
                                                className="rounded text-toggle-600 focus:ring-toggle-500 w-4 h-4"
                                            />
                                            <span className="text-sm text-slate-600 font-medium">Mark as Done</span>
                                        </label>
                                    </div>
                                </>
                            )}

                            {/* Owner Edit Date Section (legacy only) */}
                            {currentUser.id === detailTool.powerUserId && onUpdateTool && detailTool.nextTrainingDate !== undefined && getCurriculaForTool(detailTool.id).length === 0 && (
                                <div className="mb-3 p-2 bg-white rounded border border-indigo-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Owner Controls</span>
                                        <button 
                                            onClick={() => setIsEditingTraining(!isEditingTraining)}
                                            className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                                        >
                                            <Edit2 size={10} /> {isEditingTraining ? 'Cancel Edit' : 'Edit Schedule'}
                                        </button>
                                    </div>
                                    {isEditingTraining && (
                                        <div className="mt-2 flex gap-2 items-center">
                                            <input 
                                                type="date" 
                                                value={editNextDate}
                                                onChange={(e) => setEditNextDate(e.target.value)}
                                                className="flex-1 text-xs p-1 border rounded"
                                            />
                                            <button 
                                                onClick={handleSaveTrainingDate}
                                                className="text-xs bg-indigo-600 text-white px-2 py-1 rounded flex items-center gap-1 hover:bg-indigo-700"
                                            >
                                                <Save size={10} /> Save
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                         </div>
                    ) : (
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center text-slate-500 text-sm">
                            <p className="mb-2">No training materials required for this tool.</p>
                            {currentUser.id === detailTool.powerUserId && (
                                <p className="text-xs text-indigo-600 italic">
                                    Tip: As the owner, go to the Admin Panel &gt; Training to add resources.
                                </p>
                            )}
                        </div>
                    )}
                </div>
                
                <div className="p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap justify-end gap-3">
                     <button 
                        onClick={() => {
                            setTrainingRequestTool(detailTool);
                            setDetailTool(null);
                        }}
                        className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                        <GraduationCap size={16} />
                        Request Training
                    </button>
                     <button 
                        onClick={() => {
                            setFeedbackTool(detailTool);
                            setDetailTool(null);
                            setIsTrainingCompletion(false);
                        }}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                    >
                        Give Score
                    </button>
                    <button 
                        onClick={() => setDetailTool(null)}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-100 transition-colors"
                    >
                        Close
                    </button>
                </div>
             </div>
        </div>
      )}

      {/* Curriculum completion: score this formation only */}
      {curriculumToComplete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          {showSuccess ? (
            <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Training scored!</h3>
              <p className="text-sm text-slate-500">Your score has been recorded for this formation.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-800">Score this training</h3>
                <p className="text-sm text-slate-500 mt-1">{curriculumToComplete.name}</p>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Satisfaction with this training (1–10)</label>
                  <RatingSelector value={rating} onChange={setRating} />
                  <p className="text-xs text-slate-500 mt-1">Your score will be averaged with the rest of the team.</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleCurriculumScoreSubmit}
                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                  >
                    Submit score
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurriculumToComplete(null)}
                    className="py-2.5 px-4 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            {showSuccess ? (
                <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full text-center">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                        {isTrainingCompletion ? 'Training Completed!' : 'Score Submitted!'}
                    </h3>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 bg-slate-50">
                        <h3 className="text-lg font-bold text-slate-800">
                            {isTrainingCompletion ? 'Complete Training: ' : 'Score '}{feedbackTool.name}
                        </h3>
                        <p className="text-sm text-slate-500">
                            {isTrainingCompletion 
                                ? 'Please rate your initial competency after training.' 
                                : 'Help us improve the tool stack.'}
                        </p>
                    </div>
                    <form onSubmit={handleSubmitFeedback} className="p-6 space-y-6">
                        {/* Satisfaction */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">How satisfied are you with this tool?</label>
                            <RatingSelector value={rating} onChange={setRating} lowLabel="Frustrated" highLabel="Very Satisfied" />
                        </div>

                        {/* Proficiency */}
                         <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">How proficient are you with this tool?</label>
                            <RatingSelector value={proficiency} onChange={setProficiency} activeColor="emerald" lowLabel="Novice" highLabel="Expert" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Comments (Optional)</label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                className="w-full rounded-lg border-slate-300 border p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-20 resize-none"
                                placeholder="Any specific pain points?"
                            ></textarea>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={() => { setFeedbackTool(null); setIsTrainingCompletion(false); }} className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors">Cancel</button>
                            <button type="submit" className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
                                {isTrainingCompletion ? 'Complete & Save' : 'Submit Score'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
      )}

      {/* Access Request Modal */}
      {accessRequestTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
           {showSuccess ? (
                <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full text-center">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{accessRequestMode === 'change' ? 'Change Request Sent!' : 'Request Sent!'}</h3>
                    <p className="text-slate-500">{accessRequestMode === 'change' ? 'Your access change request has been sent for review.' : 'The owner has been notified.'}</p>
                </div>
           ) : (
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 bg-slate-50">
                        <h3 className="text-lg font-bold text-slate-800">{accessRequestMode === 'change' ? 'Change Access' : 'Request Access'}: {accessRequestTool.name}</h3>
                    </div>
                    <form onSubmit={handleSubmitAccess} className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                {accessRequestMode === 'change' ? 'What should change in your access?' : 'Why do you need access?'}
                            </label>
                            <textarea 
                                required
                                value={accessReason}
                                onChange={e => setAccessReason(e.target.value)}
                                className="w-full rounded-lg border-slate-300 border p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-24 resize-none"
                                placeholder={accessRequestMode === 'change'
                                  ? 'E.g. I need admin rights to manage billing exports for the Q3 close.'
                                  : 'E.g. I need to generate reports for the Q3 project.'}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">When do you need it by?</label>
                            <input 
                                type="date"
                                required
                                value={accessNeededBy}
                                onChange={e => setAccessNeededBy(e.target.value)}
                                className="w-full rounded-lg border-slate-300 border p-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={() => { setAccessRequestTool(null); setAccessRequestMode('new'); }} className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg">Cancel</button>
                            <button type="submit" className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg">{accessRequestMode === 'change' ? 'Send Change Request' : 'Send Request'}</button>
                        </div>
                    </form>
                </div>
           )}
        </div>
      )}

      {/* New Tool Request Modal */}
      {isNewToolModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
           {showSuccess ? (
                <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full text-center">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Request Submitted!</h3>
                    <p className="text-slate-500">Admin team will review your procurement request.</p>
                </div>
           ) : (
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                    <div className="p-6 border-b border-slate-100 bg-slate-50">
                        <h3 className="text-lg font-bold text-slate-800">Request New Software Procurement</h3>
                        <p className="text-sm text-slate-500">Please provide details for the approval process.</p>
                    </div>
                    <form onSubmit={handleSubmitNewTool} className="p-6 space-y-4 overflow-y-auto">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Tool Name</label>
                            <input required type="text" value={newToolData.name} onChange={e => setNewToolData({...newToolData, name: e.target.value})} className="w-full rounded-lg border-slate-300 border p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Figma" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Purpose / Business Case</label>
                            <textarea required value={newToolData.purpose} onChange={e => setNewToolData({...newToolData, purpose: e.target.value})} className="w-full rounded-lg border-slate-300 border p-2.5 text-sm outline-none h-20 resize-none focus:ring-2 focus:ring-indigo-500" placeholder="Why do we need this?" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Price Estimation</label>
                                <input required type="text" value={newToolData.priceEstimation} onChange={e => setNewToolData({...newToolData, priceEstimation: e.target.value})} className="w-full rounded-lg border-slate-300 border p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" placeholder="$50/mo" />
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Alternatives Considered</label>
                                <input required type="text" value={newToolData.alternatives} onChange={e => setNewToolData({...newToolData, alternatives: e.target.value})} className="w-full rounded-lg border-slate-300 border p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Sketch, Adobe XD" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Terms of Service URL</label>
                            <input required type="url" value={newToolData.termsUrl} onChange={e => setNewToolData({...newToolData, termsUrl: e.target.value})} className="w-full rounded-lg border-slate-300 border p-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500" placeholder="https://..." />
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <input type="checkbox" checked={newToolData.gdprCompliant} onChange={e => setNewToolData({...newToolData, gdprCompliant: e.target.checked})} className="rounded text-indigo-600 focus:ring-indigo-500" id="gdpr" />
                            <label htmlFor="gdpr" className="text-sm text-slate-700">I have checked and confirmed this tool is GDPR Compliant</label>
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-slate-100">
                            <button type="button" onClick={() => setIsNewToolModalOpen(false)} className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg">Cancel</button>
                            <button type="submit" className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg">Submit Request</button>
                        </div>
                    </form>
                </div>
           )}
        </div>
      )}

      {/* Request Training Modal */}
      {trainingRequestTool && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
             {showSuccess ? (
                <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full text-center">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Request Sent!</h3>
                    <p className="text-slate-500">Training request has been logged.</p>
                </div>
             ) : (
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 bg-slate-50">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                             <GraduationCap className="text-indigo-600" size={24} />
                             Request Training: {trainingRequestTool.name}
                        </h3>
                    </div>
                    <form onSubmit={handleSubmitTrainingRequest} className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">What specifically do you need help with?</label>
                            <textarea 
                                required
                                value={trainingNeeds}
                                onChange={e => setTrainingNeeds(e.target.value)}
                                className="w-full rounded-lg border-slate-300 border p-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-32 resize-none"
                                placeholder="E.g. I need advanced training on reporting features..."
                            />
                        </div>
                         <div className="bg-amber-50 p-3 rounded-lg border border-amber-100 text-xs text-amber-800 flex items-start gap-2">
                             <HelpCircle size={14} className="mt-0.5" />
                             Submitting this request will notify the tool owner and the training coordinator.
                         </div>
                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={() => setTrainingRequestTool(null)} className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg">Cancel</button>
                            <button type="submit" className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg font-medium">Submit Request</button>
                        </div>
                    </form>
                </div>
             )}
         </div>
      )}

      {/* QUIZ BUILDER MODAL */}
      {isQuizModalOpen && selectedManagedTool && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                  <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                      <div>
                          <h3 className="text-lg font-bold text-slate-800">Create New Exam</h3>
                          <p className="text-sm text-slate-500">For {selectedManagedTool.name}</p>
                      </div>
                      <button onClick={() => setIsQuizModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto flex-1 space-y-6">
                      <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Exam Title</label>
                          <input 
                            type="text" 
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none" 
                            placeholder="e.g. Q4 Certification"
                            value={newQuizTitle}
                            onChange={(e) => setNewQuizTitle(e.target.value)}
                          />
                      </div>

                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                          <h4 className="text-sm font-bold text-slate-800 mb-3">Questions</h4>
                          {newQuizQuestions.length > 0 && (
                              <div className="space-y-2 mb-4">
                                  {newQuizQuestions.map((q, idx) => (
                                      <div key={q.id} className="bg-white p-3 rounded border border-slate-200 text-sm flex justify-between">
                                          <span>{idx + 1}. {q.text}</span>
                                          <button 
                                            onClick={() => setNewQuizQuestions(newQuizQuestions.filter(i => i.id !== q.id))}
                                            className="text-red-500 hover:text-red-700"
                                          >
                                              <Trash2 size={14} />
                                          </button>
                                      </div>
                                  ))}
                              </div>
                          )}
                          
                          <div className="flex gap-2">
                              <input 
                                type="text" 
                                className="flex-1 p-2 text-sm border border-slate-300 rounded focus:outline-none" 
                                placeholder="Type a question..."
                                value={currentQuestionText}
                                onChange={(e) => setCurrentQuestionText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddQuestion()}
                              />
                              <button 
                                onClick={handleAddQuestion}
                                className="bg-white border border-slate-300 text-slate-600 px-3 py-2 rounded text-sm hover:bg-slate-50"
                              >
                                  Add
                              </button>
                          </div>
                      </div>
                  </div>

                  <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
                      <button onClick={() => setIsQuizModalOpen(false)} className="px-4 py-2 border border-slate-200 bg-white text-slate-700 rounded-lg hover:bg-slate-50">Cancel</button>
                      <button onClick={handleSaveQuiz} disabled={!newQuizTitle || newQuizQuestions.length === 0} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Save Exam</button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};